/**
 * ExportHistory - Historial de exportaciones e impresiones
 * Ventana flotante redimensionable con controles de minimizar/maximizar
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  ClockCounterClockwise, FilePdf, Printer, Trash, X, 
  Download, Minus, Square, CornersOut, Eye, ArrowLeft, FloppyDisk
} from '@phosphor-icons/react';
// Importar hook del contexto global
import { useExportHistory } from '@/contexts/ExportHistoryContext';
import { openSafePrintWindow, sanitizeHtml } from '../utils/safePrint';

// Re-exportar el hook para compatibilidad con imports existentes
export { useExportHistory } from '@/contexts/ExportHistoryContext';

// Componente de panel de historial - Ventana flotante redimensionable
const ExportHistoryPanel = ({ isOpen, onClose, isExternalMinimized, onMinimizedChange }) => {
  const { history, removeEntry, clearHistory, toggleMinimize } = useExportHistory();
  const [filter, setFilter] = useState('all');
  const [previewEntry, setPreviewEntry] = useState(null); // Estado para preview
  
  // Estados para ventana flotante
  const windowRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 650, height: 550 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(isExternalMinimized || false);
  const [preMaximizeState, setPreMaximizeState] = useState(null);
  const [preMinimizeState, setPreMinimizeState] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });
  
  // Sincronizar estado de minimizado con el parent
  useEffect(() => {
    if (onMinimizedChange) {
      onMinimizedChange(isMinimized);
    }
  }, [isMinimized, onMinimizedChange]);
  
  // Sincronizar desde el parent
  useEffect(() => {
    if (isExternalMinimized !== undefined && isExternalMinimized !== isMinimized) {
      setIsMinimized(isExternalMinimized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalMinimized]); // Intentionally omit isMinimized to avoid infinite loop

  // Centrar ventana al abrir
  useEffect(() => {
    if (isOpen && !isMaximized) {
      const centerX = Math.max(50, (window.innerWidth - size.width) / 2);
      const centerY = Math.max(50, (window.innerHeight - size.height) / 2);
      setPosition({ x: centerX, y: centerY });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Intentionally omit size to only center on open

  // Cursores de resize
  const resizeCursors = {
    n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
    ne: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize', sw: 'nesw-resize'
  };

  const minSize = { width: 400, height: 350 };

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.window-controls') || e.target.closest('.resize-handle')) return;
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.preventDefault();
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && !isMaximized) {
      const newX = Math.max(-size.width + 100, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    }
    
    if (isResizing && !isMaximized) {
      const deltaX = e.clientX - resizeStart.current.x;
      const deltaY = e.clientY - resizeStart.current.y;
      
      let newWidth = size.width, newHeight = size.height;
      let newX = position.x, newY = position.y;
      
      if (resizeDirection.includes('e')) newWidth = Math.max(minSize.width, resizeStart.current.width + deltaX);
      if (resizeDirection.includes('w')) {
        const widthDelta = resizeStart.current.width - deltaX;
        if (widthDelta >= minSize.width) { newWidth = widthDelta; newX = resizeStart.current.posX + deltaX; }
      }
      if (resizeDirection.includes('s')) newHeight = Math.max(minSize.height, resizeStart.current.height + deltaY);
      if (resizeDirection.includes('n')) {
        const heightDelta = resizeStart.current.height - deltaY;
        if (heightDelta >= minSize.height) { newHeight = heightDelta; newY = resizeStart.current.posY + deltaY; }
      }
      
      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing, isMaximized, resizeDirection, position, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  const handleResizeStart = useCallback((direction) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStart.current = {
      x: e.clientX, y: e.clientY,
      width: size.width, height: size.height,
      posX: position.x, posY: position.y
    };
  }, [size, position]);

  // Maximizar/Restaurar
  const handleMaximize = useCallback(() => {
    if (isMinimized) {
      // Si está minimizada, primero restaurar y luego maximizar
      setIsMinimized(false);
      setPreMaximizeState(preMinimizeState || { position, size });
      setPosition({ x: 20, y: 20 });
      setSize({ width: window.innerWidth - 40, height: window.innerHeight - 80 });
      setIsMaximized(true);
    } else if (isMaximized) {
      // Restaurar desde maximizado
      if (preMaximizeState) {
        setPosition(preMaximizeState.position);
        setSize(preMaximizeState.size);
      }
      setIsMaximized(false);
    } else {
      // Maximizar
      setPreMaximizeState({ position, size });
      setPosition({ x: 20, y: 20 });
      setSize({ width: window.innerWidth - 40, height: window.innerHeight - 80 });
      setIsMaximized(true);
    }
  }, [isMaximized, isMinimized, position, size, preMaximizeState, preMinimizeState]);

  // Minimizar - Va a la parte inferior de la pantalla
  const handleMinimize = useCallback(() => {
    if (isMinimized) {
      // Restaurar desde minimizado
      if (preMinimizeState) {
        setPosition(preMinimizeState.position);
        setSize(preMinimizeState.size);
        setIsMaximized(preMinimizeState.wasMaximized || false);
      }
      setIsMinimized(false);
    } else {
      // Minimizar - guardar estado y mover abajo
      setPreMinimizeState({ 
        position, 
        size, 
        wasMaximized: isMaximized 
      });
      setIsMaximized(false);
      setIsMinimized(true);
    }
  }, [isMinimized, position, size, isMaximized, preMinimizeState]);

  // Event listeners globales
  useEffect(() => {
    if (isDragging || isResizing) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = isResizing ? resizeCursors[resizeDirection] || 'default' : 'grabbing';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, resizeDirection]);

  if (!isOpen) return null;

  const filteredHistory = history.filter(entry => {
    if (filter === 'all') return true;
    return entry.type === filter;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'pdf': return <FilePdf size={18} className="text-red-400" />;
      case 'print': return <Printer size={18} className="text-zinc-400" />;
      case 'save': return <FloppyDisk size={18} className="text-green-400" />;
      default: return <Download size={18} className="text-gold-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'pdf': return 'PDF';
      case 'print': return 'Impresión';
      case 'save': return 'Guardado';
      default: return 'Exportación';
    }
  };

  const color = '#a855f7'; // Purple

  // Si está minimizado, renderizar solo la barra minimizada en el Taskbar
  if (isMinimized) {
    return (
      <div
        ref={windowRef}
        className="fixed rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform z-[201]"
        onClick={handleMinimize}
        style={{
          // Posición en la parte inferior, alineado con el Taskbar
          left: 'auto',
          right: 20,
          bottom: 68, // Justo encima del Taskbar (mb-3 + altura del taskbar)
          width: 260,
          height: 48,
          background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.98) 0%, rgba(5, 15, 30, 0.99) 100%)',
          border: `2px solid ${color}50`,
          boxShadow: `0 0 20px ${color}40, 0 5px 20px rgba(0, 0, 0, 0.4)`,
        }}
        data-testid="export-history-panel-minimized"
      >
        {/* Borde superior */}
        <div 
          className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl"
          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
        />
        
        {/* Header minimizado */}
        <div
          className="flex items-center justify-between px-3 py-2 h-full"
          style={{ background: `linear-gradient(90deg, ${color}15 0%, transparent 50%, ${color}08 100%)` }}
        >
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
                border: `1px solid ${color}50`,
              }}
            >
              <ClockCounterClockwise size={16} weight="fill" style={{ color }} />
            </div>
            <span className="text-sm font-bold" style={{ color }}>Historial</span>
            {history.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-500/30 text-zinc-300 border border-zinc-500/30">
                {history.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
              className="w-6 h-6 rounded-lg flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title="Restaurar"
            >
              <CornersOut size={12} className="text-gray-400 hover:text-green-400" />
            </button>
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                setIsMinimized(false);
                onClose();
              }}
              className="w-6 h-6 rounded-lg flex items-center justify-center transition-all hover:scale-110 hover:bg-red-500/20"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title="Cerrar"
            >
              <X size={12} className="text-gray-400 hover:text-red-400" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Panel completo con overlay
  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop - click para minimizar, NO para cerrar */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm cursor-pointer" 
        onClick={handleMinimize}
        title="Click para minimizar"
      />
      
      <div
        ref={windowRef}
        className="fixed rounded-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          left: position.x,
          top: position.y,
          width: isMaximized ? `calc(100vw - 40px)` : size.width,
          height: isMaximized ? `calc(100vh - 80px)` : size.height,
          background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.98) 0%, rgba(5, 15, 30, 0.99) 100%)',
          border: `2px solid ${color}50`,
          boxShadow: `0 0 40px ${color}30, 0 25px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 ${color}30`,
          cursor: isDragging ? 'grabbing' : 'default',
          transition: isResizing || isDragging ? 'none' : 'box-shadow 0.2s'
        }}
        data-testid="export-history-panel"
      >
        {/* Borde superior con gradiente */}
        <div 
          className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl"
          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
        />
        
        {/* Header - Draggable */}
        <div
          className="flex items-center justify-between select-none rounded-t-xl px-4 py-3"
          style={{
            background: `linear-gradient(90deg, ${color}15 0%, transparent 50%, ${color}08 100%)`,
            borderBottom: `1px solid ${color}30`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <div 
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
                border: `1px solid ${color}50`,
                boxShadow: `0 0 15px ${color}30`
              }}
            >
              <ClockCounterClockwise size={20} weight="fill" style={{ color, filter: `drop-shadow(0 0 6px ${color})` }} />
            </div>
            <div>
              <h3 className="font-bold tracking-wide text-base" style={{ color, textShadow: `0 0 10px ${color}60` }}>
                Historial de Exportaciones
              </h3>
              <span className="text-xs text-zinc-500">{history.length} registros</span>
            </div>
          </div>

          {/* Window controls */}
          <div className="window-controls flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {!isMaximized && (
              <span className="text-[10px] text-gray-500 mr-2 font-mono">
                {Math.round(size.width)}×{Math.round(size.height)}
              </span>
            )}
            
            {/* Minimize */}
            <button
              onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title="Minimizar"
            >
              <Minus size={14} className="text-gray-400 hover:text-yellow-400" />
            </button>
            
            {/* Maximize */}
            <button
              onClick={(e) => { e.stopPropagation(); handleMaximize(); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:scale-110"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title={isMaximized ? "Restaurar" : "Maximizar"}
            >
              {isMaximized ? (
                <CornersOut size={14} className="text-gray-400 hover:text-green-400" />
              ) : (
                <Square size={14} className="text-gray-400 hover:text-green-400" />
              )}
            </button>
            
            {/* Close */}
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:scale-110 hover:bg-red-500/20"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title="Cerrar"
            >
              <X size={14} className="text-gray-400 hover:text-red-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col" style={{ height: 'calc(100% - 60px)' }}>
          {/* Filtros */}
          <div className="flex items-center gap-2 p-4 border-b border-zinc-800/50 flex-wrap">
            {[
              { value: 'all', label: 'Todos', count: history.length },
              { value: 'save', label: 'Guardados', icon: FloppyDisk, count: history.filter(h => h.type === 'save').length },
              { value: 'pdf', label: 'PDF', icon: FilePdf, count: history.filter(h => h.type === 'pdf').length },
              { value: 'print', label: 'Impresiones', icon: Printer, count: history.filter(h => h.type === 'print').length },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  filter === f.value 
                    ? 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30 shadow-lg shadow-zinc-500/10' 
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50 border border-transparent'
                }`}
              >
                {f.icon && <f.icon size={14} />}
                  {f.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    filter === f.value ? 'bg-zinc-500/30' : 'bg-zinc-700'
                  }`}>
                    {f.count}
                  </span>
                </button>
              ))}
              
              {history.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors border border-red-500/20"
                >
                  <Trash size={14} />
                  Limpiar
                </button>
              )}
            </div>

            {/* Lista o Preview */}
            <div className="flex-1 overflow-y-auto p-4 premium-scrollbar">
              {previewEntry ? (
                /* Vista de Preview */
                <div className="h-full flex flex-col">
                  {/* Header del preview */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-zinc-800">
                    <button
                      onClick={() => setPreviewEntry(null)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
                    >
                      <ArrowLeft size={16} />
                      Volver
                    </button>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-white">{previewEntry.title}</h4>
                      <p className="text-xs text-zinc-500">
                        {new Date(previewEntry.timestamp).toLocaleString('es-ES', {
                          weekday: 'long',
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      previewEntry.type === 'pdf' 
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                        : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                    }`}>
                      {getTypeLabel(previewEntry.type)}
                    </span>
                  </div>
                  
                  {/* Contenido del preview */}
                  <div className="flex-1 overflow-auto bg-zinc-900/50 border border-zinc-800 rounded-xl">
                    {previewEntry.htmlContent ? (
                      <div 
                        className="p-4 preview-content"
                        style={{ 
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          color: 'white',
                          fontSize: '14px'
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewEntry.htmlContent) }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                        <div 
                          className="w-16 h-16 rounded-xl flex items-center justify-center mb-3"
                          style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.2)' }}
                        >
                          <Eye size={28} className="text-zinc-400/50" />
                        </div>
                        <p className="text-zinc-400 text-sm mb-1">Vista previa no disponible</p>
                        <p className="text-zinc-600 text-xs">Este registro no tiene contenido guardado</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Acciones del preview */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                    <span className="text-xs text-zinc-600">
                      {previewEntry.details || 'Sin detalles adicionales'}
                    </span>
                    <div className="flex items-center gap-2">
                      {previewEntry.htmlContent && (
                        <button
                          onClick={() => {
                            // Usar función segura de impresión (sin document.write)
                            openSafePrintWindow({
                              title: previewEntry.title,
                              content: previewEntry.htmlContent,
                              theme: 'dark'
                            });
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-500/20 text-zinc-400 hover:bg-zinc-500/30 transition-colors"
                        >
                          <Printer size={16} />
                          Reimprimir
                        </button>
                      )}
                      <button
                        onClick={() => {
                          removeEntry(previewEntry.id);
                          setPreviewEntry(null);
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      >
                        <Trash size={16} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)', border: '1px solid rgba(168, 85, 247, 0.2)' }}
                  >
                    <ClockCounterClockwise size={40} className="text-zinc-400/50" />
                  </div>
                  <p className="text-zinc-400 text-base mb-1">No hay exportaciones registradas</p>
                  <p className="text-zinc-600 text-sm">Exporta PDF o imprime desde cualquier ventana</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredHistory.map(entry => {
                    // Colores según tipo
                    const typeColors = {
                      pdf: { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.3)', badge: 'bg-red-500/20 text-red-400 border-red-500/30' },
                      print: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.3)', badge: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
                      save: { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.3)', badge: 'bg-green-500/20 text-green-400 border-green-500/30' },
                    };
                    const colors = typeColors[entry.type] || typeColors.print;
                    
                    return (
                    <div 
                      key={entry.id}
                      className="flex items-center gap-4 p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-xl hover:border-zinc-500/30 hover:bg-zinc-800/50 transition-all group"
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                        style={{ 
                          background: `linear-gradient(135deg, ${colors.bg} 0%, ${colors.bg.replace('0.2', '0.1')} 100%)`,
                          border: `1px solid ${colors.border}`
                        }}
                        onClick={() => setPreviewEntry(entry)}
                        title="Ver contenido"
                      >
                        {getTypeIcon(entry.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreviewEntry(entry)}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-bold text-white truncate">{entry.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${colors.badge}`}>
                            {getTypeLabel(entry.type)}
                          </span>
                          {entry.htmlContent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900/60 text-zinc-100 border border-gold-500/40">
                              Preview
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-500">
                          {entry.component && <span className="text-zinc-400">{entry.component}</span>}
                          {entry.component && ' • '}
                          {new Date(entry.timestamp).toLocaleString('es-ES', {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {entry.details && (
                          <div className="text-sm text-zinc-600 mt-1 truncate">{entry.details}</div>
                        )}
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="flex items-center gap-1">
                        {entry.htmlContent && (
                          <button
                            onClick={() => setPreviewEntry(entry)}
                            className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gold-500/20 text-zinc-500 hover:text-gold-400 transition-all"
                            title="Ver contenido"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => removeEntry(entry.id)}
                          className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                          title="Eliminar"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-zinc-800/50 text-xs text-zinc-600 text-center bg-zinc-900/50">
              Se guardan hasta 50 exportaciones • Arrastra los bordes para redimensionar
            </div>
          </div>

        {/* Resize handles */}
        {!isMaximized && (
          <>
            <div className="resize-handle absolute top-0 left-4 right-4 h-2 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.n }} onMouseDown={handleResizeStart('n')} />
            <div className="resize-handle absolute bottom-0 left-4 right-4 h-3 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.s }} onMouseDown={handleResizeStart('s')} />
            <div className="resize-handle absolute left-0 top-4 bottom-4 w-2 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.w }} onMouseDown={handleResizeStart('w')} />
            <div className="resize-handle absolute right-0 top-4 bottom-4 w-3 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.e }} onMouseDown={handleResizeStart('e')} />
            <div className="resize-handle absolute top-0 left-0 w-5 h-5 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.nw }} onMouseDown={handleResizeStart('nw')} />
            <div className="resize-handle absolute top-0 right-0 w-5 h-5 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.ne }} onMouseDown={handleResizeStart('ne')} />
            <div className="resize-handle absolute bottom-0 left-0 w-5 h-5 hover:bg-zinc-500/20" style={{ cursor: resizeCursors.sw }} onMouseDown={handleResizeStart('sw')} />
            
            {/* Esquina inferior derecha visible */}
            <div
              className="resize-handle absolute bottom-0 right-0 w-6 h-6 group"
              style={{ cursor: resizeCursors.se }}
              onMouseDown={handleResizeStart('se')}
            >
              <div className="absolute inset-0 rounded-tl-lg bg-zinc-500/0 group-hover:bg-zinc-500/20 transition-colors" />
              <svg width="16" height="16" viewBox="0 0 16 16" className="absolute bottom-0.5 right-0.5 opacity-40 group-hover:opacity-100 transition-all" style={{ color }}>
                <path d="M14 14L14 8M14 14L8 14M11 14L14 11M14 5L5 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExportHistoryPanel;
